import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { toDocumentDetail, toDocumentSummary } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { DocumentListItem, DocumentRead } from '@/shared/api/contracts'
import { isApiError } from '@/shared/api/errors'
import { qk } from '@/shared/api/query-keys'
import { useUserIndex } from '@/shared/hooks/use-users'
import { toSearchParams } from '@/shared/lib/query-params'

import type {
  DocumentCreateInput,
  DocumentDetail,
  DocumentFilters,
  DocumentPatchInput,
} from './types'

/**
 * Conflicto de edición.
 *
 * El backend contesta 409 cuando `expected_version` quedó vieja, y el cuerpo
 * trae **el documento entero** en `error.details.current`. La pantalla necesita
 * las DOS versiones para mostrarlas lado a lado: "alguien guardó antes que vos"
 * sin decir qué escribió no sirve para decidir nada.
 *
 * Antes eso se resolvía con un GET extra después del 409, que además podía
 * traer una TERCERA versión si alguien guardaba en el medio. Ahora la versión
 * que se muestra es exactamente la que rechazó el guardado.
 */
export class DocumentConflictError extends Error {
  readonly current: DocumentDetail

  constructor(current: DocumentDetail) {
    super('Alguien guardó una versión más nueva de este documento.')
    this.name = 'DocumentConflictError'
    this.current = current
  }
}

export function isConflictError(error: unknown): error is DocumentConflictError {
  return error instanceof DocumentConflictError
}

export function useDocuments(filters: DocumentFilters) {
  const enabled = Boolean(filters.company_id ?? filters.project_id)
  const users = useUserIndex()

  return useQuery({
    queryKey: filters.project_id
      ? qk.projects.documents(filters.project_id, filters)
      : qk.companies.documents(filters.company_id ?? '', filters),
    queryFn: async () => {
      const items = await api.get<DocumentListItem[]>(
        `documents${toSearchParams(filters as Record<string, string>)}`,
      )
      // `DocumentListItem` no repite `company_id` (ya venía en el filtro).
      return items.map((item) => toDocumentSummary(item, filters.company_id ?? '', users))
    },
    enabled,
  })
}

export function useDocument(documentId: string) {
  const users = useUserIndex()

  return useQuery({
    queryKey: qk.documents.detail(documentId),
    queryFn: async () =>
      toDocumentDetail(await api.get<DocumentRead>(`documents/${documentId}`), users),
    enabled: Boolean(documentId),
  })
}

export function useCreateDocument() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (input: DocumentCreateInput) =>
      toDocumentDetail(await api.post<DocumentRead>('documents', { ...input })),
    onSuccess: (created) => {
      void client.invalidateQueries({ queryKey: qk.companies.detail(created.company_id) })
      if (created.project_id) {
        void client.invalidateQueries({ queryKey: qk.projects.detail(created.project_id) })
      }
    },
  })
}

export function useUpdateDocument(documentId: string) {
  const client = useQueryClient()
  const users = useUserIndex()

  return useMutation({
    /**
     * `expected_version` **viaja**: es el control de concurrencia optimista.
     * Omitirlo es elegir el comportamiento viejo —el último en guardar gana, en
     * silencio—, que es la forma más cara de perder media hora de escritura.
     */
    mutationFn: async (patch: DocumentPatchInput): Promise<DocumentDetail> => {
      try {
        return toDocumentDetail(
          await api.patch<DocumentRead>(`documents/${documentId}`, { ...patch }),
          users,
        )
      } catch (error) {
        if (isApiError(error) && error.isConflict) {
          // El 409 ya trae el documento actual completo. Sólo si un proxy se
          // comió el cuerpo hace falta la segunda ida.
          const current = error.details.current as DocumentRead | undefined
          throw new DocumentConflictError(
            toDocumentDetail(
              current ?? (await api.get<DocumentRead>(`documents/${documentId}`)),
              users,
            ),
          )
        }
        throw error
      }
    },
    onSuccess: (saved) => {
      client.setQueryData(qk.documents.detail(documentId), saved)
      void client.invalidateQueries({ queryKey: qk.companies.detail(saved.company_id) })
      if (saved.project_id) {
        void client.invalidateQueries({ queryKey: qk.projects.documents(saved.project_id) })
      }
    },
  })
}
